import "../styles/App.css";
import { useSearchParams, useNavigate } from "react-router-dom";
import { DiscoveryDisplayNotification } from "./DiscoveryDisplayNotification";
import { DiscoveryInputMemo } from "./DiscoveryInputMemo";
import { CONSTANTS } from '../constants';
import { useEffect, useState } from "react";
import { DiscoverySubmitButton } from "./DiscoverySubmitButton";
import { useMsal, useAccount } from "@azure/msal-react";
import { protectedResources } from "../authConfig";

export const DataDisplay = (props) => {
    const { instance, accounts, inProgress } = useMsal();
    const account = useAccount(accounts[0] || {});
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryString = searchParams.get('q');
    // 通知に関する定数
    const { REF01, REQ01 } = CONSTANTS.ALERT_MESSAGE;
    // 入力されたテキストを管理するstate
    const [inputText, setInputText] = useState("");
    const [tokenResponse, setTokenResponse] = useState(null);

    // テキストフィールドの値が変更されたときに呼び出される関数
    const handleTextFieldChange = (event) => {
        // stateを更新する
        setInputText(event.target.value);
    };

    // 送信ボタンがクリックされたときに呼び出される関数
    const handleSubmitButtonClick = async () => {
        // Approval API を呼び出して、承認リソースを作成します。
        const apiEndpoint = `${protectedResources.approvalApi.endpoint}/${queryString}`;
        const custom_authorization_header = process.env["REACT_APP_AAD_APP_CUSTOM_AUTHORIZATION_HEADER"];
        const reqHeaders = new Headers();
        reqHeaders.append('Content-Type', 'application/json');
        reqHeaders.append(custom_authorization_header, 'Bearer ' + tokenResponse.accessToken);
        const options = {
            method: 'POST',
            headers: reqHeaders,
            body: inputText
        };

        // API実行
        await fetch(apiEndpoint, options)
            .then((res) => res.json())
            .then((json) => {
                navigate(`/approval?q=${json.LocationId}&p=${json.partitionKey}&r=${json.rowKey}`)
                return (json);
            })
            .catch((e) => {
                console.log(e);
            });
    };

    useEffect(() => {
        if (account && inProgress === "none") {
            instance.acquireTokenSilent({
                scopes: protectedResources.approvalApi.scopes,
                account: account
            }).then(async (res) => {
                console.log(res);
                const newTokenResponse = res;
                setTokenResponse(newTokenResponse);
            }).catch((error) => {
                console.log(error);
            });
        }
    }, []);

    // value がオブジェクト、かつキーに 'hasApprove' が存在しない場合、value の内容をテーブル形式で表示する。
    if ((typeof props.secretData.value == "object") && !('hasApprove' in props.secretData.value)) {
        const tableRows = tableRowsCreate(props.secretData.value, 0);
        console.log(tableRows);

        return (
            <>
                <div className="notification-area-div">
                    <DiscoveryDisplayNotification severity="warning" message="表示されたデータには機密情報が含まれますので、取扱いについては充分注意してください。他者との共有は厳禁です。" />
                </div>
                <div className="data-area-div">
                    <table>
                        <thead>
                        </thead>
                        <tbody>
                            {tableRows}
                        </tbody>
                    </table>
                </div>
            </>
        );
    } else {
        if ((typeof props.secretData.value == "object") && ('hasApprove' in props.secretData.value)) {
            return (
                <>
                    <div className="notification-area-div">
                        <DiscoveryDisplayNotification severity={REQ01.severity} message={REQ01.message} />
                    </div>
                    {(props.secretData.value.hasApprove && props.secretData.value.code == null)
                        ?
                        <>
                            <div className="data-area-div">
                                <table>
                                    <thead>
                                        <tr><th>承認者</th></tr>
                                    </thead>
                                    <tbody>
                                        {props.secretData.value.location.approvers.map((approver, index) => (
                                            <tr key={index}><td><i><mark>{approver.name}({approver.upn})</mark></i></td></tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="memo-area-div">
                                <DiscoveryInputMemo value={inputText} onChange={handleTextFieldChange} />
                            </div>

                            <div className="button-area-div">
                                <DiscoverySubmitButton locationId={queryString} memo={inputText} onClick={handleSubmitButtonClick} />
                            </div>
                        </>
                        :
                        navigate(`/approval?q=${props.secretData.value.approval.LocationId}&p=${props.secretData.value.approval.partitionKey}&r=${props.secretData.value.approval.rowKey}`)
                    }
                </>
            );
        } else {
            return (
                <>
                    <div className="notification-area-div">
                        <DiscoveryDisplayNotification severity="error" message={props.secretData.value} />
                    </div>
                </>
            );
        }
    }

    /**
     * JSONデータからテーブルの行要素を構成します。
     *
     * APIから受け取ったキーと値をHTML文字列へ埋め込まず、
     * Reactの子要素として描画します。
     * これにより、HTMLエンティティの誤解釈と
     * 意図しないHTMLの挿入を防止します。
     *
     * @param {object} json 表示対象のJSONデータ
     * @param {number} count 呼び出し回数
     * @param {string} parentKey 親要素を識別するためのキー
     * @returns {JSX.Element[]}
     */
    function tableRowsCreate(json, count, parentKey = "root") {
        const result = [];
        const indent = count;

        Object.entries(json).forEach(([key, value]) => {
            const rowKey = `${parentKey}-${key}`;

            if (value !== null && typeof value === "object") {
                if (Array.isArray(value)) {
                    value.forEach((item, index) => {
                        const itemKey = `${rowKey}-${index}`;

                        result.push(
                            <tr key={itemKey}>
                                {index === 0 && (
                                    <td
                                        rowSpan={value.length}
                                        style={{ textIndent: `${indent}em` }}
                                    >
                                        <b>{key}</b>
                                    </td>
                                )}
                                <td>
                                    <i>
                                        <mark>{String(item)}</mark>
                                    </i>
                                </td>
                            </tr>
                        );
                    });
                } else {
                    result.push(
                        <tr key={rowKey}>
                            <td
                                colSpan={2}
                                style={{ textIndent: `${indent}em` }}
                            >
                                <b>{key}</b>
                            </td>
                        </tr>
                    );

                    result.push(
                        ...tableRowsCreate(value, count + 1, rowKey)
                    );
                }
            } else {
                result.push(
                    <tr key={rowKey}>
                        <td style={{ textIndent: `${indent}em` }}>
                            <b>{key}</b>
                        </td>
                        <td>
                            <i>
                                <mark>{String(value ?? "")}</mark>
                            </i>
                        </td>
                    </tr>
                );
            }
        });

        return result;
    }
}